import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { get } from 'lodash-es';
import { DateTime } from 'luxon';
import * as i1 from '@upr-web-primer/dal';
import { DalModule } from '@upr-web-primer/dal';
import * as i2 from '@upr-web-primer/locale';
import { LocaleModule } from '@upr-web-primer/locale';
import { HttpHeaders } from '@angular/common/http';
const PlacesUrls = {
  GET_PLACE: '/resort-areas/{resortAreaCode}/places/{placeId}',
  SEARCH_PLACES: '/resort-areas/{resortAreaCode}/search-places'
};
class MsPlacesCommonUtilityService {
  constructor(appDefaults) {
    this.appDefaults = appDefaults;
    this.timeFormats = ['hh:mm a', 'HH:mm'];
    this.timeZone = this.appDefaults.UPR_TIME_ZONE;
  }
  /**
   * Get todays open/close time from place data.
   *
   * @param place
   * @returns
   */
  getTodaysHours(place) {
    const periods = get(place, 'place_hours.periods');
    if (!periods || !Array.isArray(periods)) {
      return;
    }
    const currentDay = this.getCurrentWeekDay();
    for (const period of periods) {
      const {
        open,
        close
      } = period;
      const openDay = open.day === 0 ? 7 : open.day;
      if (currentDay === openDay) {
        return this.getHoursFromFormats(open, close);
      }
    }
  }
  /**
   * Get open/close times for up to the next 7 days from today.
   *
   * @param place
   * @param transform - function add to custom mapping of open/close DateTime objects
   * @returns
   */
  getWeekHours(place, transform) {
    const periods = get(place, 'place_hours.periods');
    if (!periods || !Array.isArray(periods)) {
      return;
    }
    const mappedHours = periods.slice(0, 7).reduce((mappedPeriods, {
      open,
      close
    }) => {
      const hours = this.getHoursFromFormats(open, close);
      if (!hours) {
        return mappedPeriods;
      }
      if (transform) {
        mappedPeriods.set(hours.open, transform(hours));
      } else {
        mappedPeriods.set(hours.open, hours);
      }
      return mappedPeriods;
    }, new Map());
    return Array.from(mappedHours.keys()).sort((hour1, hour2) => {
      const diff = hour1.toUnixInteger() - hour2.toUnixInteger();
      if (diff < 0) {
        return -1;
      } else if (diff > 0) {
        return 1;
      }
      return 0;
    }).map(date => mappedHours.get(date));
  }
  /**
   * Get open/close times from supported hour formatting used by Tridion authors.
   *
   * @param open
   * @param close
   * @returns
   */
  getHoursFromFormats(open, close) {
    const currentDay = this.getCurrentWeekDay();
    const openDay = open.day === 0 ? 7 : open.day;
    const closeDay = close.day === 0 ? 7 : close.day;
    let openDayDiff = openDay - currentDay;
    let closeDayDiff = closeDay - currentDay;
    let openDateTime;
    let closeDateTime;
    if (openDayDiff < 0) {
      openDayDiff += 7;
    }
    if (closeDayDiff < 0) {
      closeDayDiff += 7;
    }
    for (const format of this.timeFormats) {
      const openTime = DateTime.fromFormat(open.time, format, {
        zone: this.timeZone
      });
      const closeTime = DateTime.fromFormat(close.time, format, {
        zone: this.timeZone
      });
      if (openTime.isValid && closeTime.isValid) {
        openDateTime = openDayDiff === 0 ? openTime : openTime.plus({
          days: 7 - openDayDiff
        });
        closeDateTime = closeDayDiff === 0 ? closeTime : closeTime.plus({
          days: 7 - closeDayDiff
        });
        break;
      }
    }
    if (!openDateTime || !closeDateTime) {
      return;
    }
    return {
      open: openDateTime,
      close: closeDateTime
    };
  }
  /**
   * Get todays weekday.
   *
   * @returns number
   */
  getCurrentWeekDay() {
    return DateTime.now().setZone(this.timeZone).weekday;
  }
  static {
    this.ɵfac = function MsPlacesCommonUtilityService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MsPlacesCommonUtilityService)(i0.ɵɵinject('AppDefaults'));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: MsPlacesCommonUtilityService,
      factory: MsPlacesCommonUtilityService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsPlacesCommonUtilityService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }], null);
})();
class MsPlacesModule {
  static {
    this.ɵfac = function MsPlacesModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MsPlacesModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: MsPlacesModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [DalModule, LocaleModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsPlacesModule, [{
    type: NgModule,
    args: [{
      imports: [DalModule, LocaleModule]
    }]
  }], null, null);
})();
class MsPlacesService {
  constructor(dal, localeService, appDefaults) {
    this.dal = dal;
    this.localeService = localeService;
    this.appDefaults = appDefaults;
  }
  getPlaceById(id) {
    const relativeUrl = this.replaceResortAreaCode(this.replacePlaceId(PlacesUrls.GET_PLACE, id));
    const httpOptions = this.createHeaders();
    return this.dal.httpRequest(relativeUrl, 'GET', {}, httpOptions);
  }
  searchPlaces(body) {
    const relativeUrl = this.replaceResortAreaCode(PlacesUrls.SEARCH_PLACES);
    const httpOptions = this.createHeaders();
    return this.dal.httpRequest(relativeUrl, 'POST', body, httpOptions);
  }
  createHeaders() {
    const locale = this.localeService.getFromStorage();
    const language = locale?.language;
    const country = locale?.country?.toUpperCase();
    return new HttpHeaders({
      'Accept-Language': `${language}-${country}` + (language === 'es' ? ',en-US' : '')
    });
  }
  /**
   * @private method to replace the resort area code in the url
   * @memberof MsPlacesService
   * @param url
   * @returns string by replacing {resortAreaCode}
   **/
  replaceResortAreaCode(url) {
    return url ? url.replace('{resortAreaCode}', this.appDefaults.PERSONALIZE_RESORT_CODE) : '';
  }
  /**
   * @private method to replace the place id in the url
   * @memberof MsPlacesService
   * @param url
   * @returns string by replacing {placeId}
   **/
  replacePlaceId(url, id) {
    return url ? url.replace('{placeId}', id) : '';
  }
  static {
    this.ɵfac = function MsPlacesService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MsPlacesService)(i0.ɵɵinject(i1.DalService), i0.ɵɵinject(i2.LocaleService), i0.ɵɵinject('AppDefaults'));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: MsPlacesService,
      factory: MsPlacesService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsPlacesService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.DalService
  }, {
    type: i2.LocaleService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }], null);
})();

/*
 * Public API Surface of ms-places
 */

/**
 * Generated bundle index. Do not edit.
 */

export { MsPlacesCommonUtilityService, MsPlacesModule, MsPlacesService, PlacesUrls };
